import { useDispatch, useSelector } from 'react-redux'
import {
  selectActiveAddress,
  selectCharacteristicsCurrentContractActive,
  selectCurrentAddressData,
  selectPceSearchData,
  selectPersonalContactSubscriptionData,
  selectPreferencesContactSubscriptionData,
  selectProfessionalContactSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import {
  updateGasMeterAddressData,
  updatePceSearchData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import {
  PulseSearchModalOverrides,
  SearchModalOverrides,
} from '@/containers/SubscriptionFormPageNew/Components/shared/MeterSearch/styled-components'
import usePulseScope from '@/shared/components/PulseScope/usePulseScope'
import { canSearchWithName, streetNumberDigits } from './pceSearchRules'
import { meterSearchAddress } from '@/containers/SubscriptionFormPageNew/Components/shared/MeterSearch/meterSearchAddress'

const MOVE_IN_PRESTATIONS = ['mes', 'fmes']

const usePceSearchV2 = () => {
  const isPulse = usePulseScope()
  const dispatch = useDispatch()
  const activeAddress = useSelector(selectActiveAddress)
  const currentAddress = useSelector(selectCurrentAddressData)
  const { firstName, lastName } = useSelector(selectPersonalContactSubscriptionData) ?? {}
  const { clientType } = useSelector(selectPreferencesContactSubscriptionData) ?? {}
  const { socialReason } = useSelector(selectProfessionalContactSubscriptionData) ?? {}
  const { prestationType } = useSelector(selectCharacteristicsCurrentContractActive) ?? {}
  const {
    address,
    viewPage,
    occupant,
    pceSelected,
    serialNumber,
    searchPodResponses,
    searchPodResponsesOriginal,
    isOpen,
    isLoading,
    page,
  } = useSelector(selectPceSearchData)

  const { zipcode = '', inseeCode = '', city = '' } = address
  // The address search gives null for a missing part, and a destructuring default only replaces undefined.
  const streetNumber = address.streetNumber ?? ''
  const streetName = address.streetName ?? ''
  const searchedStreetNumber = streetNumberDigits(streetNumber)
  const pceSearchParams = { inseeCode, streetNumber: searchedStreetNumber, streetName, occupant, serialNumber }

  const customerLastName = clientType === 'pro' ? socialReason : lastName
  const isCustomerNamed = firstName?.trim().length > 0 && customerLastName?.trim().length > 0
  const isMoveIn = MOVE_IN_PRESTATIONS.includes(prestationType)
  const holderLabelKey = isMoveIn
    ? 'subscriptionTools:pce_search.fields.previous_occupant'
    : 'subscriptionTools:pce_search.fields.current_holder'

  const resetPceSearchData = () => {
    dispatch(
      updatePceSearchData({
        searchPodResponses: [],
        searchPodResponsesOriginal: [],
        viewPage: 'search',
        address: {},
        occupant: '',
        serialNumber: '',
        page: 1,
        errorMessage: null,
        podSearchDataId: null,
      }),
    )
  }

  const updateList = results => {
    dispatch(
      updatePceSearchData({
        searchPodResponses: results,
        searchPodResponsesOriginal: results,
        triggerSearchPod: false,
        page: 1,
      }),
    )
  }

  const updatePceConfigData = payload => {
    dispatch(updatePceSearchData(payload))
  }

  const updatePce = pceNo => {
    dispatch(updateGasMeterAddressData({ data: { pceNo }, id: 'activeAddress' }))
  }

  const openSearch = () => {
    const prefilledAddress = meterSearchAddress(address, currentAddress)
    const prefilledHolder = !isMoveIn && !occupant && customerLastName ? { occupant: customerLastName } : {}
    dispatch(
      updatePceSearchData({
        isOpen: true,
        ...(prefilledAddress
          ? { address: { ...prefilledAddress, streetNumber: streetNumberDigits(prefilledAddress.streetNumber) } }
          : {}),
        ...prefilledHolder,
      }),
    )
  }

  const updateAddressHandler = address => {
    if (!address) {
      dispatch(updatePceSearchData({ address: {} }))
      return
    }
    dispatch(
      updatePceSearchData({
        address: address[0]
          ? { ...address[0], streetNumber: streetNumberDigits(address[0].streetNumber) }
          : {
              streetName: '',
              label: '',
              city: '',
              inseeCode: '',
              zipcode: '',
              streetNumber: '',
              type: '',
            },
      }),
    )
  }

  const streetNumberKeyDownHandler = event => {
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !/\d/.test(event.key)) {
      event.preventDefault()
    }
  }

  const streetNumberBlurHandler = value => {
    updatePceConfigData({ address: { ...address, streetNumber: streetNumberDigits(value) } })
  }

  const nameAllowsSearch = canSearchWithName({ occupant: occupant ?? '', serialNumber: serialNumber ?? '' })
  const isFormFilled = isCustomerNamed && Object.keys(address).length > 0 && nameAllowsSearch

  const handlePageChange = nextPage => {
    if (nextPage < 1) {
      return
    }
    if (nextPage > Math.ceil(searchPodResponses.length / 8)) {
      return
    }
    dispatch(updatePceSearchData({ page: nextPage }))
  }

  const handleSelectPce = pceData => {
    dispatch(
      updateGasMeterAddressData({
        data: {
          pceNo: pceData.pce || pceData.pod_id,
          isValidPce: true,
          meterConsent: false,
          consentDate: null,
        },
        id: activeAddress,
      }),
    )
    updatePceConfigData({ isOpen: false })
    resetPceSearchData()
  }

  const handleBackToSearch = () => {
    updatePceConfigData({ viewPage: 'search' })
  }

  return {
    isFormFilled,
    isCustomerNamed,
    holderLabelKey,
    modalOverrides: isPulse ? PulseSearchModalOverrides : SearchModalOverrides,
    currentViewPage: viewPage,
    getAddress: address,
    getZipCode: zipcode,
    getInseeCode: inseeCode,
    getCity: city,
    getOccupant: occupant,
    getSerialNumber: serialNumber,
    currentSelectedOption: pceSelected,
    searchPodResponses,
    searchPodResponsesOriginal,
    getStreetNumberName: `${searchedStreetNumber} ${streetName}`.trim(),
    getStreetNumber: streetNumber,
    getStreetName: streetName,
    pceSearchParams,
    resetPceSearchData,
    isOpen,
    isLoading,
    updateList,
    updatePceConfigData,
    updateAddressHandler,
    streetNumberKeyDownHandler,
    streetNumberBlurHandler,
    openSearch,
    updatePce,
    handlePageChange,
    page,
    handleSelectPce,
    handleBackToSearch,
  }
}

export default usePceSearchV2
